import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Edit, Trash2 } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import EditUserDialog from './editusers/EditUserDialog'
import DeleteUserDialog from './deleteusers/DeleteUserDialog'
import PaginationComponent from '@/components/common/PaginationComponent'
import userService from '@/services/userService'
import { formatTableText } from '@/lib/tableUtils'

const UserTable = ({ 
  users, 
  setUsers, 
  currentPage, 
  totalPages, 
  totalUsers, 
  pageSize, 
  onPageChange,
  onDeleteUser,
  isTransporter
}) => {
  const { toast } = useToast()
  const [editingUser, setEditingUser] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const getStatusVariant = (status) => {
    return status === 'Active' ? 'active' : 'inactive'
  }

  const getRoleVariant = (role) => {
    switch (role) {
      case 'Admin':
      case 'Super Admin':
        return 'admin'
      case 'Port Admin':
        return 'admin'
      case 'Transporter':
        return 'transporter'
      case 'Distributor':
        return 'distributor'
      case 'Driver':
        return 'driver'
      case 'Retailer':
        return 'retailer'
      case 'Account Officer':
        return 'default'
      default:
        return 'default'
    }
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setIsEditDialogOpen(true)
  }

  const handleUpdateUser = async (updatedUserData) => {
    try {
      const updatedUserResponse = await userService.updateUser(updatedUserData.id, updatedUserData)
      
      const formatRole = (r) => {
        if (!r) return 'User';
        const clean = r.toLowerCase();
        if (clean === 'field_officer') return 'Field Officer';
        if (clean === 'account_head') return 'Account Head';
        if (clean === 'region_head') return 'Region Head';
        if (clean === 'ipl_admin') return 'IPL Admin';
        return r
          .replace('ipl_', '')
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      };

      const transformedUser = {
        id: updatedUserResponse.id,
        name: updatedUserResponse.username,
        email: updatedUserResponse.email,
        phone_number: updatedUserResponse.phone_number || '',
        role: formatRole(updatedUserResponse.role),
        status: updatedUserResponse.is_active ? 'Active' : 'Inactive',
        lastLogin: updatedUserResponse.created_at ? new Date(updatedUserResponse.created_at).toLocaleString() : 'Never'
      }
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === transformedUser.id ? transformedUser : user
        )
      )
      
      setIsEditDialogOpen(false)
      setEditingUser(null)
      toast({
        title: "User Updated",
        description: `${transformedUser.name} has been successfully updated.`,
      })
    } catch (err) {
      console.error('Failed to update user:', err)
      toast({
        title: "Error",
        description: "Failed to update user. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleDeleteUser = (user) => {
    setUserToDelete(user)
    setIsDeleteDialogOpen(true)
  }

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      try {
        await onDeleteUser(userToDelete.id)
        
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id))
        
        setIsDeleteDialogOpen(false)
        setUserToDelete(null)
        toast({
          title: "User Deleted",
          description: `${userToDelete.name} has been successfully deleted.`,
        })
      } catch (err) {
        console.error('Failed to delete user:', err)
        setIsDeleteDialogOpen(false)
        setUserToDelete(null)
        
        if (err.message && err.message.includes("Super admin access required")) {
          toast({
            title: "Permission Denied",
            description: "Super admin access required. Please contact a super admin to delete this user.",
            variant: "destructive",
          })
        } else {
          toast({
            title: "Error",
            description: `Failed to delete user: ${err.message || 'Please try again.'}`,
            variant: "destructive",
          })
        }
      }
    }
  }

  const cancelDeleteUser = () => {
    setIsDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  return (
    <>
      <div className="rounded-md border overflow-hidden w-full">
        <div className="max-h-[calc(100vh-250px)] overflow-auto w-full">
          <Table>
            <TableHeader className=" sticky top-0 z-10">
              <TableRow>
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Username</TableHead>
                {!isTransporter && (
                  <TableHead className="h-10 px-4 py-1 min-w-[200px]">Email</TableHead>
                )}
                <TableHead className="h-10 px-4 py-1 min-w-[120px]">Phone</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[120px]">Role</TableHead>
                <TableHead className="h-10 px-6 py-1 min-w-[100px]">Status</TableHead>
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Created At</TableHead>
                <TableHead className="h-10 px-4 py-1 text-right min-w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="hover:bg-transparent border-border">
                  <TableCell className="h-10 px-4 py-1 font-medium">{formatTableText(user.name) || 'N/A'}</TableCell>
                  {!isTransporter && (
                    <TableCell className="h-10 px-4 py-1">{formatTableText(user.email) || 'N/A'}</TableCell>
                  )}
                  <TableCell className="h-10 px-4 py-1">{formatTableText(user.phone_number) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1">
                    <Badge
                      variant={getRoleVariant(user.role)}
                      className="inline-block text-center w-[100px]"
                    >
                      {user.role || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell className="h-10 px-4 py-1">
                    <Badge
                      variant={getStatusVariant(user.status)}
                      className="inline-block text-center w-[80px]"
                    >
                      {user.status || 'N/A'}
                    </Badge>
                  </TableCell>
                  <TableCell className="h-10 px-4 py-1">{formatTableText(user.lastLogin) || 'N/A'}</TableCell>
                  <TableCell className="h-10 px-4 py-1 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          <Edit className="mr-2 h-4 w-4" />
                          {isTransporter ? 'Edit Driver' : 'Edit User'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteUser(user)} className="text-red-500">
                          <Trash2 className="mr-2 h-4 w-4" />
                          {isTransporter ? 'Delete Driver' : 'Delete User'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalUsers}
          pageSize={pageSize}
          onPageChange={onPageChange}
          className="border-t"
        />
      </div>

      <EditUserDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        user={editingUser}
        onUpdateUser={handleUpdateUser}
        isTransporter={isTransporter}
      />

      <DeleteUserDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        user={userToDelete}
        onDeleteUser={confirmDeleteUser}
        isTransporter={isTransporter}
      />
    </>
  )
}

export default UserTable